import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { AuthService, AUTH_TOKEN_STORAGE_KEY } from '../auth';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Logout } from '../store/actions/auth.actions';
import { AppState } from '../store/states/app.state';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { logAuthError, logAuthWarn } from '../auth/auth-debug.helpers';
import { environment } from '../environments/environment';
import { sessionConfigDefaults } from '../environments/session.config';

/** Shared across tabs so activity in one tab resets idle countdown everywhere. */
export const SESSION_LAST_ACTIVITY_KEY = 'session_last_activity';

@Injectable({
  providedIn: 'root'
})
export class SessionWarningService implements OnDestroy {
  private warningSubject = new BehaviorSubject<{ show: boolean; timeLeft: number }>({
    show: false,
    timeLeft: 0
  });
  public warning$ = this.warningSubject.asObservable();

  private checkInterval?: Subscription;
  private tokenSyncSub?: Subscription;

  private readonly sessionConfig = {
    ...sessionConfigDefaults,
    ...environment.session
  };
  private readonly idleBeforeWarningMs =
    this.sessionConfig.idleMinutesBeforeWarning * 60 * 1000;
  private readonly idleWarningDurationMs =
    this.sessionConfig.idleWarningMinutes * 60 * 1000;
  private readonly tokenRefreshBeforeExpirySeconds =
    this.sessionConfig.tokenRefreshBeforeExpirySeconds;

  private lastActivityAt = Date.now();
  private lastActivityRecordedAt = 0;
  private readonly activityThrottleMs = 1000;
  private silentRenewInFlight = false;
  private lastSilentRenewAttempt = 0;
  private readonly silentRenewCooldownMs = 15_000;

  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

  private readonly onStorageSync = (event: StorageEvent): void => {
    if (event.key === AUTH_TOKEN_STORAGE_KEY) {
      this.authService.syncTokenFromStorage();
      void this.evaluateSession();
      return;
    }
    if (event.key === SESSION_LAST_ACTIVITY_KEY && event.newValue) {
      const ts = Number.parseInt(event.newValue, 10);
      if (!Number.isNaN(ts) && ts > this.lastActivityAt) {
        this.lastActivityAt = ts;
        this.warningSubject.next({ show: false, timeLeft: 0 });
      }
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (typeof document === 'undefined' || document.hidden) {
      return;
    }
    this.authService.syncTokenFromStorage();
    void this.evaluateSession();
  };

  private readonly onUserActivity = (): void => {
    this.recordUserActivity();
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    private store: Store<AppState>,
    private modalService: NgbModal
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorageSync);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      for (const eventName of this.activityEvents) {
        window.addEventListener(eventName, this.onUserActivity, { passive: true });
      }
    }
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorageSync);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      for (const eventName of this.activityEvents) {
        window.removeEventListener(eventName, this.onUserActivity);
      }
    }
  }

  startMonitoring(): void {
    this.stopMonitoring();
    this.recordUserActivity(true);

    this.tokenSyncSub = this.authService.tokenSync$.subscribe(() => {
      void this.evaluateSession();
    });

    this.checkInterval = interval(1000).subscribe(() => {
      void this.evaluateSession();
    });
  }

  stopMonitoring(): void {
    this.tokenSyncSub?.unsubscribe();
    this.tokenSyncSub = undefined;
    this.checkInterval?.unsubscribe();
    this.checkInterval = undefined;
    this.silentRenewInFlight = false;
    this.warningSubject.next({ show: false, timeLeft: 0 });
  }

  /** User chose to stay — same effect as mouse/keyboard activity. */
  stayActive(): void {
    this.recordUserActivity(true);
  }

  /** @deprecated Use stayActive(). Kept for compatibility. */
  async renewSession(): Promise<boolean> {
    this.stayActive();
    return true;
  }

  handleLogout(): void {
    this.forceLogout('session-warning: user logout');
  }

  recordUserActivity(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastActivityRecordedAt < this.activityThrottleMs) {
      return;
    }
    this.lastActivityRecordedAt = now;
    this.lastActivityAt = now;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
    }
    this.warningSubject.next({ show: false, timeLeft: 0 });
  }

  private getEffectiveLastActivity(): number {
    let stored = 0;
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
      stored = raw ? Number.parseInt(raw, 10) : 0;
    }
    if (Number.isNaN(stored)) {
      stored = 0;
    }
    return Math.max(this.lastActivityAt, stored);
  }

  private async evaluateSession(): Promise<void> {
    const token = this.authService.syncTokenFromStorage();
    if (!token) {
      this.warningSubject.next({ show: false, timeLeft: 0 });
      return;
    }

    await this.ensureTokenFresh();

    const lastActivity = this.getEffectiveLastActivity();
    const idleMs = Date.now() - lastActivity;
    const warningStartMs = this.idleBeforeWarningMs;
    const logoutAtMs = this.idleBeforeWarningMs + this.idleWarningDurationMs;

    if (idleMs >= logoutAtMs) {
      this.forceLogout('session-warning: idle timeout (25 min)');
      return;
    }

    if (idleMs >= warningStartMs) {
      const secondsLeft = Math.ceil((logoutAtMs - idleMs) / 1000);
      this.warningSubject.next({
        show: true,
        timeLeft: Math.max(0, secondsLeft)
      });
      return;
    }

    this.warningSubject.next({ show: false, timeLeft: 0 });
  }

  /** Silent refresh when access token is near expiry or already expired. */
  private async ensureTokenFresh(): Promise<void> {
    const token = this.authService.syncTokenFromStorage();
    if (!token || !this.authService.hasRefreshToken(token)) {
      return;
    }

    const timeLeft = this.authService.getAccessTokenSecondsRemaining(token);
    const needsRefresh =
      !this.authService.isTokenValid(token) ||
      (timeLeft !== null && timeLeft <= this.tokenRefreshBeforeExpirySeconds);

    if (needsRefresh) {
      await this.trySilentRenew();
    }
  }

  private async trySilentRenew(): Promise<boolean> {
    if (this.silentRenewInFlight) {
      return false;
    }
    const now = Date.now();
    if (now - this.lastSilentRenewAttempt < this.silentRenewCooldownMs) {
      return false;
    }

    this.silentRenewInFlight = true;
    this.lastSilentRenewAttempt = now;
    try {
      this.authService.syncTokenFromStorage();
      const newToken = await this.authService.refreshToken().toPromise();
      return !!(newToken && this.authService.isTokenValid(newToken));
    } catch (error) {
      logAuthError('session-warning: silent token refresh error', error);
      return false;
    } finally {
      this.silentRenewInFlight = false;
    }
  }

  private forceLogout(reason: string): void {
    logAuthWarn(reason);
    this.stopMonitoring();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
    }
    this.store.dispatch(new Logout());
    setTimeout(() => {
      this.modalService.dismissAll();
      this.router.navigate(['/login2']);
    }, 50);
  }
}
